import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Submission, Skill, User } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Search, BarChart2, TrendingUp, Grid, List, Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const Reports: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'progress' | 'search'>('progress');
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [skills, setSkills] = useState<Skill[]>([]);
    const [engineers, setEngineers] = useState<User[]>([]);
    const [reviews, setReviews] = useState<any[]>([]);

    // Search State
    const [selectedSkills, setSelectedSkills] = useState<string[]>([]); // Changed to array for multi-select
    const [skillDropdownOpen, setSkillDropdownOpen] = useState(false); // Toggle dropdown
    const [skillFilterText, setSkillFilterText] = useState(''); // Search filter
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]); // Category multi-select
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false); // Category dropdown toggle
    const [categoryFilterText, setCategoryFilterText] = useState(''); // Category search filter
    const [minScore, setMinScore] = useState(1);
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [viewMode, setViewMode] = useState<'card' | 'grid'>('card');

    // Progress State
    const [selectedEngineer, setSelectedEngineer] = useState('');
    const [employeeSearchText, setEmployeeSearchText] = useState(''); // For searchable employee dropdown
    const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false); // Employee dropdown toggle
    const [employeeFilterText, setEmployeeFilterText] = useState(''); // Employee search filter
    const [progressData, setProgressData] = useState<Submission[]>([]);
    const [selectedSkillsForChart, setSelectedSkillsForChart] = useState<string[]>([]); // Skills to show on chart

    useEffect(() => {
        if (!user) return;

        const loadData = async () => {
            // Get employee hierarchy (self + subordinates)
            const employees = await apiService.getEmployeeHierarchy();
            console.log('Loaded employees with Dept/Division:', employees);
            setEngineers(employees);
            setSelectedEngineer(user.id); // Auto-select self

            // Get submissions for skill search
            // Fetch submissions for all employees in the hierarchy
            const allSubmissionsPromises = employees.map(emp =>
                apiService.getSubmissionsByEmployee(emp.id).catch(() => [])
            );

            const submissionsArrays = await Promise.all(allSubmissionsPromises);
            const allSubmissions = submissionsArrays.flat();

            // Deduplicate by submission ID
            const uniqueSubmissions = allSubmissions.filter((sub, index, self) =>
                index === self.findIndex(s => s.id === sub.id)
            );

            setSubmissions(uniqueSubmissions);
            console.log('Loaded submissions for search:', uniqueSubmissions.length, 'from', employees.length, 'employees');

            // Load skills
            apiService.getSkills().then(setSkills);
        };

        loadData();
    }, [user]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if click is outside all dropdowns
            if (!target.closest('.dropdown-container')) {
                setCategoryDropdownOpen(false);
                setSkillDropdownOpen(false);
                setEmployeeDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleExport = () => {
        if (searchResults.length === 0) return;

        // Create CSV content
        const headers = [
            'Engineer Name',
            'Division',
            'Department',
            'Skill Name',
            'Period',
            'Period Type',
            'Self Score',
            'Manager Score',
            'Achievement Notes',
            'Manager Comment',
            'Review Status',
            'Submission Status',
            'Last Updated Date'
        ];

        const rows = searchResults.map(res => [
            res.engineerName,
            res.divisionName || '',
            res.departmentName || '',
            res.skillName,
            res.period,
            res.periodType,
            res.selfScore,
            res.managerScore || '',
            `"${(res.achievementNotes || '').replace(/"/g, '""')}"`, // Escape quotes
            `"${(res.managerComment || '').replace(/"/g, '""')}"`,
            res.reviewStatus,
            res.submissionStatus,
            new Date(res.lastUpdatedDate).toLocaleDateString()
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `skill_search_report_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handlePivotedExport = async () => {
        // Use employee hierarchy instead of search results
        if (engineers.length === 0) {
            alert('No employees found in hierarchy');
            return;
        }

        // Get all employee IDs from hierarchy (all subordinates)
        const employeeIds = engineers.map(e => e.id);

        // Fetch all submissions for these employees to get ALL their skills
        const allSubmissionsPromises = employeeIds.map(empId =>
            apiService.getSubmissionsByEmployee(empId).catch(() => [])
        );
        const allSubmissionsArrays = await Promise.all(allSubmissionsPromises);
        const allSubmissions = allSubmissionsArrays.flat();

        // Fetch reviews for all submissions
        const reviewsPromises = allSubmissions.map(sub =>
            apiService.getReviewBySubmissionId(sub.id).catch(() => null)
        );
        const reviews = await Promise.all(reviewsPromises);

        // Group results by employee with ALL their skills
        const employeeMap = new Map<string, any>();

        allSubmissions.forEach((sub, idx) => {
            const key = sub.engineerId;
            const engineer = engineers.find(e => e.id === sub.engineerId);

            if (!employeeMap.has(key)) {
                employeeMap.set(key, {
                    name: sub.engineerName || engineer?.name || 'Unknown',
                    division: engineer?.divisionName || '',
                    department: engineer?.departmentName || '',
                    skills: new Map<string, { self: number, manager: number | null }>()
                });
            }

            const employee = employeeMap.get(key)!;
            const review = reviews[idx];

            // Add all skills from this submission
            sub.details.forEach(detail => {
                const skill = skills.find(s => s.id === detail.skillId);
                const skillName = skill ? `${skill.name} (${skill.category})` : 'Unknown';
                const reviewDetail = review?.details.find((rd: any) => rd.skillId === detail.skillId);

                // Keep the highest rating if skill appears in multiple submissions
                const existing = employee.skills.get(skillName);
                if (!existing || detail.selfScore > existing.self) {
                    employee.skills.set(skillName, {
                        self: detail.selfScore,
                        manager: reviewDetail?.managerScore || null
                    });
                }
            });
        });

        // Get all unique skills across all employees
        const allSkills = new Set<string>();
        employeeMap.forEach(emp => {
            emp.skills.forEach((_: any, skillName: string) => allSkills.add(skillName));
        });
        const skillsList = Array.from(allSkills).sort();

        // Create headers
        const headers = ['Name', 'Division', 'Department'];
        skillsList.forEach(skill => {
            headers.push(`${skill} (Self)`);
            headers.push(`${skill} (Manager)`);
        });

        // Create rows
        const rows: string[][] = [];
        employeeMap.forEach(emp => {
            const row = [emp.name, emp.division, emp.department];

            skillsList.forEach(skill => {
                const rating = emp.skills.get(skill);
                if (rating) {
                    row.push(rating.self.toString());
                    row.push(rating.manager !== null ? rating.manager.toString() : '');
                } else {
                    row.push('');
                    row.push('');
                }
            });

            rows.push(row);
        });

        // Create CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell =>
                typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
            ).join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `skill_matrix_report_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleSearch = async () => {
        if (selectedSkills.length === 0) {
            console.log('No skills selected');
            return;
        }

        console.log('Starting search...', {
            selectedSkills,
            minScore,
            submissionsCount: submissions.length
        });

        // Search through submissions and fetch review data
        const results = await Promise.all(
            submissions.flatMap(async (sub) => {
                // Filter details that match any of the selected skills
                const matchingDetails = sub.details.filter(
                    d => selectedSkills.includes(d.skillId) && d.selfScore >= minScore
                );

                if (matchingDetails.length === 0) return [];

                // Fetch review for this submission
                const review = await apiService.getReviewBySubmissionId(sub.id);

                return matchingDetails.map(d => {
                    const reviewDetail = review?.details.find((rd: any) => rd.skillId === d.skillId);
                    // Get engineer info from engineers list
                    const engineer = engineers.find(e => e.id === sub.engineerId);
                    const engineerName = sub.engineerName || engineer?.name || 'Unknown';
                    const skill = skills.find(s => s.id === d.skillId);
                    console.log('Engineer data for', engineerName, ':', engineer);

                    return {
                        engineerName: engineerName,
                        engineerId: sub.engineerId,
                        departmentName: engineer?.departmentName || '',
                        divisionName: engineer?.divisionName || '',
                        skillName: skill ? `${skill.name} (${skill.category})` : 'Unknown',
                        period: sub.period,
                        periodType: sub.periodType,
                        selfScore: d.selfScore,
                        managerScore: reviewDetail?.managerScore || null,
                        managerComment: reviewDetail?.managerComment || null,
                        achievementNotes: d.achievementNotes,
                        submissionStatus: sub.status,
                        reviewStatus: review?.status || 'Not Reviewed',
                        lastUpdatedDate: sub.updatedAt
                    };
                });
            })
        );

        const flatResults = results.flat();
        console.log('Search results before deduplication:', flatResults.length, flatResults);

        // Deduplicate: Keep only the latest submission for each User-Skill combination
        const deduplicatedMap = new Map<string, any>();

        flatResults.forEach(result => {
            const key = `${result.engineerId}-${result.skillName}`;
            const existing = deduplicatedMap.get(key);

            if (!existing || new Date(result.lastUpdatedDate) > new Date(existing.lastUpdatedDate)) {
                deduplicatedMap.set(key, result);
            }
        });

        const deduplicatedResults = Array.from(deduplicatedMap.values());
        console.log('Search results after deduplication:', deduplicatedResults.length, deduplicatedResults);
        setSearchResults(deduplicatedResults);
    };


    const handleProgressFilter = async () => {
        if (!selectedEngineer) return;

        // Fetch submissions for the selected employee
        const employeeSubmissions = await apiService.getSubmissionsByEmployee(selectedEngineer);
        const data = employeeSubmissions.sort((a, b) => a.period.localeCompare(b.period));
        setProgressData(data);

        // Fetch reviews for each submission
        const reviewsData = await Promise.all(
            data.map(sub => apiService.getReviewBySubmissionId(sub.id))
        );
        setReviews(reviewsData.filter(r => r !== null));

        // Auto-select all skills for the chart by default
        const submittedSkillIds = new Set<string>();
        data.forEach(sub => {
            sub.details.forEach(detail => submittedSkillIds.add(detail.skillId));
        });
        setSelectedSkillsForChart(Array.from(submittedSkillIds));
    };


    // Prepare chart data for the selected engineer
    const getChartData = () => {
        if (progressData.length === 0) return { barData: [], submittedSkills: [] };

        // Get all unique skills that were submitted
        const submittedSkillIds = new Set<string>();
        progressData.forEach(sub => {
            sub.details.forEach(detail => submittedSkillIds.add(detail.skillId));
        });

        const submittedSkills = skills.filter(s => submittedSkillIds.has(s.id));

        // Get chart data showing skill progression over time with both self and manager scores
        const barData = progressData.map(sub => {
            const dataPoint: any = { period: sub.period };
            const review = reviews.find(r => r && r.submissionId === sub.id);

            sub.details.forEach(detail => {
                const skillName = skills.find(s => s.id === detail.skillId)?.name || 'Unknown';
                dataPoint[`${skillName} (Self)`] = detail.selfScore;

                // Add manager score if review exists
                const reviewDetail = review?.details.find((rd: any) => rd.skillId === detail.skillId);
                if (reviewDetail) {
                    dataPoint[`${skillName} (Manager)`] = reviewDetail.managerScore;
                }
            });
            return dataPoint;
        });

        return { barData, submittedSkills };
    };


    // Extract unique categories and filter skills based on selected categories
    const uniqueCategories = Array.from(new Set(skills.map(s => s.category))).sort();
    const filteredSkillsByCategory = selectedCategories.length > 0
        ? skills.filter(s => selectedCategories.includes(s.category))
        : skills;

    const { barData, submittedSkills } = getChartData();

    // Get all skills from the latest submission for table display
    const getSkillsTableData = () => {
        if (progressData.length === 0) return [];
        const latestSubmission = progressData[progressData.length - 1];
        const latestReview = reviews.find(r => r && r.submissionId === latestSubmission.id);

        return latestSubmission.details.map(detail => {
            const reviewDetail = latestReview?.details.find((rd: any) => rd.skillId === detail.skillId);
            return {
                skillName: skills.find(s => s.id === detail.skillId)?.name || 'Unknown',
                selfScore: detail.selfScore,
                managerScore: reviewDetail?.managerScore || null,
                notes: detail.achievementNotes
            };
        });
    };

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Reports & Analytics</h1>

            <div className="flex space-x-4 border-b border-gray-200">
                <button
                    className={`pb-2 px-4 font-medium ${activeTab === 'progress' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('progress')}
                >
                    Employee Progress
                </button>
                <button
                    className={`pb-2 px-4 font-medium ${activeTab === 'search' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('search')}
                >
                    Skill Search
                </button>
            </div>

            {activeTab === 'progress' && (
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center"><BarChart2 className="mr-2" /> Employee Progress Report</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-4 items-end">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
                                    <div className="relative dropdown-container">
                                        <div
                                            className="w-full rounded-md border border-gray-300 p-2 bg-white text-gray-900 min-h-[42px] cursor-pointer hover:border-gray-400"
                                            onClick={() => setEmployeeDropdownOpen(!employeeDropdownOpen)}
                                        >
                                            <span className={selectedEngineer ? "text-gray-900" : "text-gray-400"}>
                                                {selectedEngineer ? engineers.find(e => e.id === selectedEngineer)?.name : "-- Select Employee --"}
                                            </span>
                                        </div>

                                        {employeeDropdownOpen && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                                {/* Search Input */}
                                                <div className="p-2 border-b border-gray-200">
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="Search employees..."
                                                        value={employeeFilterText}
                                                        onChange={(e) => setEmployeeFilterText(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>

                                                {/* Employees List */}
                                                <div className="max-h-60 overflow-y-auto">
                                                    {engineers
                                                        .filter(emp => emp.name.toLowerCase().includes(employeeFilterText.toLowerCase()))
                                                        .map(employee => (
                                                            <div
                                                                key={employee.id}
                                                                className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                                onClick={() => {
                                                                    setSelectedEngineer(employee.id);
                                                                    setEmployeeSearchText(employee.name);
                                                                    setEmployeeDropdownOpen(false);
                                                                    setEmployeeFilterText('');
                                                                }}
                                                            >
                                                                <span className="text-sm text-gray-900">{employee.name}</span>
                                                            </div>
                                                        ))}
                                                    {engineers.filter(emp => emp.name.toLowerCase().includes(employeeFilterText.toLowerCase())).length === 0 && (
                                                        <div className="px-3 py-2 text-sm text-gray-500">No employees found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <Button onClick={handleProgressFilter}>Generate Report</Button>
                            </div>
                        </CardContent>
                    </Card>

                    {progressData.length > 0 && (
                        <>
                            {/* Skills Table with Scores */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        <TrendingUp className="mr-2 h-5 w-5" />
                                        Latest Skills Assessment
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill Name</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self Score</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Score</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Achievement Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {getSkillsTableData().map((skill, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{skill.skillName}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-sm font-semibold text-blue-600">{skill.selfScore}/5</span>
                                                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="bg-blue-600 h-2 rounded-full"
                                                                        style={{ width: `${skill.selfScore * 10}%` }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {skill.managerScore !== null ? (
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-sm font-semibold text-green-600">{skill.managerScore}/5</span>
                                                                    <div className="w-24 bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-green-600 h-2 rounded-full"
                                                                            style={{ width: `${skill.managerScore * 10}%` }}
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-sm text-gray-400 italic">Not reviewed</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">{skill.notes}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>


                            {/* Line Chart - Skills Progress Over Time */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Skills Progress Over Time</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {/* Skill Filter Section */}
                                    {submittedSkills.length > 0 && (
                                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between mb-3">
                                                <label className="text-sm font-medium text-gray-700">
                                                    Select Skills to Display ({selectedSkillsForChart.length} of {submittedSkills.length} selected)
                                                </label>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => setSelectedSkillsForChart(submittedSkills.map(s => s.id))}
                                                        className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded border border-blue-300"
                                                    >
                                                        Select All
                                                    </button>
                                                    <button
                                                        onClick={() => setSelectedSkillsForChart([])}
                                                        className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded border border-gray-300"
                                                    >
                                                        Clear All
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                {submittedSkills.map((skill) => (
                                                    <label
                                                        key={skill.id}
                                                        className="flex items-center space-x-2 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSkillsForChart.includes(skill.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedSkillsForChart([...selectedSkillsForChart, skill.id]);
                                                                } else {
                                                                    setSelectedSkillsForChart(selectedSkillsForChart.filter(id => id !== skill.id));
                                                                }
                                                            }}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <span className="text-sm text-gray-700">{skill.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Chart */}
                                    {selectedSkillsForChart.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                            <p className="text-lg font-medium">No skills selected</p>
                                            <p className="text-sm mt-1">Please select at least one skill to view the progress chart</p>
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={400}>
                                            <LineChart data={barData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                <XAxis dataKey="period" tick={{ fill: '#6b7280', fontSize: 12 }} />
                                                <YAxis domain={[0, 5]} tick={{ fill: '#6b7280', fontSize: 12 }} />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '6px',
                                                        fontSize: '12px'
                                                    }}
                                                />
                                                <Legend
                                                    wrapperStyle={{ fontSize: '12px' }}
                                                    iconType="line"
                                                />
                                                {submittedSkills
                                                    ?.filter(skill => selectedSkillsForChart.includes(skill.id))
                                                    .flatMap((skill, idx) => {
                                                        // Use a better color palette
                                                        const colors = [
                                                            '#3b82f6', // blue
                                                            '#10b981', // green
                                                            '#f59e0b', // amber
                                                            '#ef4444', // red
                                                            '#8b5cf6', // purple
                                                            '#ec4899', // pink
                                                            '#14b8a6', // teal
                                                            '#f97316', // orange
                                                        ];
                                                        const color = colors[idx % colors.length];

                                                        return [
                                                            <Line
                                                                key={`${skill.id}-self`}
                                                                type="monotone"
                                                                dataKey={`${skill.name} (Self)`}
                                                                name={`${skill.name} (Self)`}
                                                                stroke={color}
                                                                strokeWidth={2}
                                                                dot={{ r: 4, fill: color }}
                                                                activeDot={{ r: 6 }}
                                                            />,
                                                            <Line
                                                                key={`${skill.id}-manager`}
                                                                type="monotone"
                                                                dataKey={`${skill.name} (Manager)`}
                                                                name={`${skill.name} (Manager)`}
                                                                stroke={color}
                                                                strokeWidth={2}
                                                                strokeDasharray="5 5"
                                                                dot={{ r: 4, fill: color, strokeDasharray: 'none' }}
                                                                activeDot={{ r: 6 }}
                                                            />
                                                        ];
                                                    })}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}

                                    {/* Legend explanation */}
                                    {selectedSkillsForChart.length > 0 && (
                                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <p className="text-xs text-blue-800">
                                                <strong>Note:</strong> Solid lines represent self-scores, dashed lines represent manager scores.
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Submission History Table */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Submission History</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill Name</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self Score</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Score</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submission Date</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewed Date</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {progressData.flatMap(sub => {
                                                    const review = reviews.find(r => r.submissionId === sub.id);

                                                    return sub.details.map((detail, idx) => {
                                                        const skill = skills.find(s => s.id === detail.skillId);
                                                        const reviewDetail = review?.details.find((rd: any) => rd.skillId === detail.skillId);

                                                        return (
                                                            <tr key={`${sub.id}-${idx}`} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sub.period}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    {skill ? `${skill.name} (${skill.category})` : 'Unknown'}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-semibold">
                                                                    {detail.selfScore}/5
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                                                                    {reviewDetail?.managerScore !== null && reviewDetail?.managerScore !== undefined ? `${reviewDetail.managerScore}/5` : '-'}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    {new Date(sub.createdAt).toLocaleDateString()}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    {review?.updatedAt ? new Date(review.updatedAt).toLocaleDateString() : '-'}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${sub.status === 'Reviewed' ? 'bg-green-100 text-green-800' :
                                                                        sub.status === 'Submitted' ? 'bg-blue-100 text-blue-800' :
                                                                            'bg-yellow-100 text-yellow-800'
                                                                        }`}>
                                                                        {sub.status}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    });
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>
            )}

            {activeTab === 'search' && (
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center"><Search className="mr-2" /> Skill Search</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-4 items-end">
                                {/* Category Dropdown */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Categories (Select Multiple)</label>
                                    <div className="relative dropdown-container">
                                        {/* Selected Categories Display / Dropdown Toggle */}
                                        <div
                                            className="w-full rounded-md border border-gray-300 p-2 bg-white text-gray-900 min-h-[42px] cursor-pointer hover:border-gray-400"
                                            onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                                        >
                                            {selectedCategories.length === 0 ? (
                                                <span className="text-gray-400">-- All Categories --</span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1">
                                                    {selectedCategories.map(category => (
                                                        <span
                                                            key={category}
                                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {category}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedCategories(selectedCategories.filter(c => c !== category));
                                                                }}
                                                                className="ml-1 text-purple-600 hover:text-purple-800"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Dropdown Menu */}
                                        {categoryDropdownOpen && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                                {/* Search Input */}
                                                <div className="p-2 border-b border-gray-200">
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                        placeholder="Search categories..."
                                                        value={categoryFilterText}
                                                        onChange={(e) => setCategoryFilterText(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>

                                                {/* Select All Option */}
                                                <div className="p-2 border-b border-gray-200 bg-gray-50">
                                                    <label className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer rounded" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCategories.length === uniqueCategories.length && uniqueCategories.length > 0}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedCategories([...uniqueCategories]);
                                                                } else {
                                                                    setSelectedCategories([]);
                                                                }
                                                            }}
                                                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                                        />
                                                        <span className="ml-2 text-sm font-semibold text-gray-900">Select All</span>
                                                    </label>
                                                </div>

                                                {/* Categories List */}
                                                <div className="max-h-60 overflow-y-auto">
                                                    {uniqueCategories
                                                        .filter(cat => cat.toLowerCase().includes(categoryFilterText.toLowerCase()))
                                                        .map(category => (
                                                            <label
                                                                key={category}
                                                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedCategories.includes(category)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedCategories([...selectedCategories, category]);
                                                                        } else {
                                                                            setSelectedCategories(selectedCategories.filter(c => c !== category));
                                                                        }
                                                                    }}
                                                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-900">{category}</span>
                                                            </label>
                                                        ))
                                                    }
                                                    {uniqueCategories.filter(cat => cat.toLowerCase().includes(categoryFilterText.toLowerCase())).length === 0 && (
                                                        <div className="px-3 py-2 text-sm text-gray-500">No categories found</div>
                                                    )}
                                                </div>

                                                {/* Close Button */}
                                                <div className="p-2 border-t border-gray-200 flex justify-end">
                                                    <button
                                                        onClick={() => {
                                                            setCategoryDropdownOpen(false);
                                                            setCategoryFilterText('');
                                                        }}
                                                        className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                                                    >
                                                        Done
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Skills Dropdown */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Skills (Select Multiple)</label>
                                    <div className="relative dropdown-container">
                                        {/* Selected Skills Display / Dropdown Toggle */}
                                        <div
                                            className="w-full rounded-md border border-gray-300 p-2 bg-white text-gray-900 min-h-[42px] cursor-pointer hover:border-gray-400"
                                            onClick={() => setSkillDropdownOpen(!skillDropdownOpen)}
                                        >
                                            {selectedSkills.length === 0 ? (
                                                <span className="text-gray-400">-- Click to select skills --</span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1">
                                                    {selectedSkills.map(skillId => {
                                                        const skill = filteredSkillsByCategory.find(s => s.id === skillId);
                                                        return skill ? (
                                                            <span
                                                                key={skillId}
                                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {skill.name}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedSkills(selectedSkills.filter(id => id !== skillId));
                                                                    }}
                                                                    className="ml-1 text-blue-600 hover:text-blue-800"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ) : null;
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* Dropdown Menu */}
                                        {skillDropdownOpen && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                                {/* Search Input */}
                                                <div className="p-2 border-b border-gray-200">
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="Search skills..."
                                                        value={skillFilterText}
                                                        onChange={(e) => setSkillFilterText(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>

                                                {/* Select All Option */}
                                                <div className="p-2 border-b border-gray-200 bg-gray-50">
                                                    <label className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer rounded" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSkills.length === filteredSkillsByCategory.length && filteredSkillsByCategory.length > 0}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedSkills(filteredSkillsByCategory.map(s => s.id));
                                                                } else {
                                                                    setSelectedSkills([]);
                                                                }
                                                            }}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <span className="ml-2 text-sm font-semibold text-gray-900">Select All</span>
                                                    </label>
                                                </div>

                                                {/* Skills List */}
                                                <div className="max-h-60 overflow-y-auto">
                                                    {filteredSkillsByCategory
                                                        .filter(skill =>
                                                            skill.name.toLowerCase().includes(skillFilterText.toLowerCase())
                                                        )
                                                        .map(skill => (
                                                            <label
                                                                key={skill.id}
                                                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedSkills.includes(skill.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedSkills([...selectedSkills, skill.id]);
                                                                        } else {
                                                                            setSelectedSkills(selectedSkills.filter(id => id !== skill.id));
                                                                        }
                                                                    }}
                                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-900">{skill.name}</span>
                                                            </label>
                                                        ))
                                                    }
                                                    {filteredSkillsByCategory.filter(skill =>
                                                        skill.name.toLowerCase().includes(skillFilterText.toLowerCase())
                                                    ).length === 0 && (
                                                            <div className="px-3 py-2 text-sm text-gray-500">No skills found</div>
                                                        )}
                                                </div>

                                                {/* Close Button */}
                                                <div className="p-2 border-t border-gray-200 flex justify-end">
                                                    <button
                                                        onClick={() => {
                                                            setSkillDropdownOpen(false);
                                                            setSkillFilterText('');
                                                        }}
                                                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                                    >
                                                        Done
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <Input
                                        label="Min Score"
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={minScore}
                                        onChange={(e) => setMinScore(parseInt(e.target.value))}
                                    />
                                </div>
                                <Button onClick={handleSearch}>Search</Button>
                            </div>
                        </CardContent>
                    </Card>

                    {searchResults.length > 0 && (
                        <div className="space-y-4">
                            {/* View Toggle and Export Controls */}
                            <div className="flex justify-end items-center gap-2">
                                <div className="flex bg-gray-100 p-1 rounded-lg">
                                    <button
                                        onClick={() => setViewMode('card')}
                                        className={`p-2 rounded-md transition-all ${viewMode === 'card' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                        title="Card View"
                                    >
                                        <Grid size={20} />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                        title="Grid View"
                                    >
                                        <List size={20} />
                                    </button>
                                </div>
                                <Button onClick={handleExport} variant="outline" className="flex items-center gap-2">
                                    <Download size={18} />
                                    Export Detail
                                </Button>
                                <Button onClick={handlePivotedExport} variant="outline" className="flex items-center gap-2">
                                    <Download size={18} />
                                    Export Matrix
                                </Button>
                            </div>

                            {viewMode === 'card' ? (
                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    {searchResults.map((res, idx) => (
                                        <Card key={idx}>
                                            <CardContent className="pt-6">
                                                <div className="space-y-3">
                                                    <div>
                                                        <h3 className="font-bold text-lg text-gray-900">{res.engineerName}</h3>
                                                        <p className="text-sm text-gray-500">{res.period} ({res.periodType})</p>
                                                        <div className="flex gap-2 mt-1">
                                                            {res.divisionName && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{res.divisionName}</span>}
                                                            {res.departmentName && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{res.departmentName}</span>}
                                                        </div>
                                                    </div>

                                                    <div className="bg-gray-50 p-2 rounded">
                                                        <p className="text-xs text-gray-600 mb-1">Skill</p>
                                                        <p className="text-sm font-semibold text-gray-900">{res.skillName}</p>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div className="bg-blue-50 p-3 rounded">
                                                            <p className="text-xs text-gray-600 mb-1">Self Score</p>
                                                            <p className="text-2xl font-bold text-blue-600">{res.selfScore}/5</p>
                                                        </div>
                                                        <div className={`p-3 rounded ${res.managerScore ? 'bg-green-50' : 'bg-gray-50'}`}>
                                                            <p className="text-xs text-gray-600 mb-1">Manager Score</p>
                                                            <p className={`text-2xl font-bold ${res.managerScore ? 'text-green-600' : 'text-gray-400'}`}>
                                                                {res.managerScore ? `${res.managerScore}/5` : 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <p className="text-xs font-medium text-gray-700 mb-1">Achievement Notes:</p>
                                                        <p className="text-sm text-gray-600 italic bg-gray-50 p-2 rounded">"{res.achievementNotes}"</p>
                                                    </div>

                                                    {res.managerComment && (
                                                        <div>
                                                            <p className="text-xs font-medium text-gray-700 mb-1">Manager Feedback:</p>
                                                            <p className="text-sm text-gray-600 bg-green-50 p-2 rounded">"{res.managerComment}"</p>
                                                        </div>
                                                    )}

                                                    <div className="flex gap-2 pt-2 border-t">
                                                        <span className={`px-2 py-1 text-xs font-medium rounded ${res.submissionStatus === 'Reviewed' ? 'bg-green-100 text-green-800' :
                                                            res.submissionStatus === 'Submitted' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-yellow-100 text-yellow-800'
                                                            }`}>
                                                            {res.submissionStatus}
                                                        </span>
                                                        <span className={`px-2 py-1 text-xs font-medium rounded ${res.reviewStatus === 'Completed' ? 'bg-green-100 text-green-800' :
                                                            res.reviewStatus === 'In Review' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-gray-100 text-gray-600'
                                                            }`}>
                                                            Review: {res.reviewStatus}
                                                        </span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <Card>
                                    <CardContent className="p-0">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Division</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill Name</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self Rating</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Rating</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {searchResults.map((res, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">{res.engineerName}</div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                {res.divisionName || '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                {res.departmentName || '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {res.skillName}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-sm font-semibold text-blue-600">{res.selfScore}/5</span>
                                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-blue-600 h-2 rounded-full"
                                                                            style={{ width: `${res.selfScore * 10}%` }}
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                {res.managerScore !== null ? (
                                                                    <div className="flex items-center space-x-2">
                                                                        <span className="text-sm font-semibold text-green-600">{res.managerScore}/5</span>
                                                                        <div className="w-16 bg-gray-200 rounded-full h-2">
                                                                            <div
                                                                                className="bg-green-600 h-2 rounded-full"
                                                                                style={{ width: `${res.managerScore * 10}%` }}
                                                                            ></div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-sm text-gray-400 italic">Not reviewed</span>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                {new Date(res.lastUpdatedDate).toLocaleDateString()}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
